import React, { useState, useEffect } from 'react';
import Button from '../shared/Button';
import Modal from '../shared/Modal';
import ConfirmDialog from '../shared/ConfirmDialog';
import { useToast } from '../../hooks/useToast';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import {
    getTransporterTypes,
    createTransporterTypeConfig,
    deleteTransporterTypeConfig
} from '../../services/firestore/systemConfigService';
import { getNextSequenceNumber } from '../../services/firestore/sequenceService';

const TransporterTypeConfig = ({ user }) => {
    const { success, error: toastError } = useToast();
    const { confirmConfig, showConfirm, closeConfirm } = useConfirmDialog();
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (user?.tenantId) {
            loadData();
        }
    }, [user?.tenantId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await getTransporterTypes(user.tenantId);
            setTypes(data);
        } catch (err) {
            toastError('Veriler yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        setFormData({ tenantId: user.tenantId, name: '', code: '' });
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (!formData.name) return;

        try {
            let code = formData.code;

            // Auto-generate code if empty using sequence 'TRANSPORTER_TYPE'
            if (!code) {
                const nextSeq = await getNextSequenceNumber(user.tenantId, 'TRANSPORTER_TYPE');
                if (nextSeq) {
                    code = nextSeq;
                } else {
                    // Fallback
                    code = formData.name.toUpperCase().replace(/ /g, '_').replace(/[^\w-]+/g, '');
                }
            }

            await createTransporterTypeConfig({
                ...formData,
                code
            });
            success('Tedarikçi türü eklendi');
            setIsModalOpen(false);
            loadData();
        } catch (err) {
            toastError('Hata: ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: 'Tedarikçi Türünü Sil',
            message: 'Bu tedarikçi türünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                try {
                    await deleteTransporterTypeConfig(id);
                    success('Silindi');
                    loadData();
                } catch (err) {
                    toastError('Silinemedi');
                }
            }
        });
    };

    return (
        <div>
            <div className="flex justify-end mb-md">
                <Button variant="primary" onClick={handleOpenModal}>+ Yeni Tedarikçi Türü</Button>
            </div>

            <div className="card" style={{ padding: 0 }}>
                {loading ? (
                    <div className="p-lg text-center">Yükleniyor...</div>
                ) : types.length === 0 ? (
                    <div className="p-lg text-center text-secondary">Kayıt yok.</div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Tür Adı</th>
                                <th>Kod</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {types.map(t => (
                                <tr key={t.id}>
                                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                                    <td className="text-secondary">{t.code}</td>
                                    <td>
                                        <Button size="sm" variant="danger" onClick={() => handleDelete(t.id)}>Sil</Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Yeni Tedarikçi Türü">
                <div className="form-group">
                    <label className="form-label">Tür Adı</label>
                    <input
                        className="input"
                        value={formData.name || ''}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Örn: Global Lojistik"
                    />
                </div>
                <div className="form-group">
                    <label className="form-label">Kod (Opsiyonel)</label>
                    <input
                        className="input"
                        value={formData.code || ''}
                        onChange={e => setFormData({ ...formData, code: e.target.value })}
                        placeholder="Boş bırakılırsa 'TRANSPORTER_TYPE' aralığından üretilir"
                    />
                </div>
                <div className="flex justify-end gap-md mt-lg">
                    <Button variant="secondary" onClick={() => setIsModalOpen(false)}>İptal</Button>
                    <Button variant="primary" onClick={handleSave}>Kaydet</Button>
                </div>
            </Modal>

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={closeConfirm}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default TransporterTypeConfig;
